// src/components/logistique/elementsLogistique.jsx
// Les pièces communes du registre du parc : champs, badges d'état, photo
// protégée, choix d'une photo à l'appareil.

import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Camera, ImagePlus, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { BOUTON_CONTOUR } from '../ui/boutons';
import ListeDeroulante from '../ui/ListeDeroulante';
import FenetrePhotoCamera, { cameraDansLaPage } from './FenetrePhotoCamera';
import { parc } from './apiLogistique';
import { ETATS, libelleEtat } from './formatLogistique';

export const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 disabled:bg-gray-50 disabled:text-gray-500';
export const ZONE_TEXTE = 'w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
export const LIBELLE = 'mb-1 block text-sm text-gray-700';
// Les couleurs des boutons sont celles de KLASYS (ui/boutons.js), reprises ici
// pour que les fenêtres du module les trouvent avec leurs champs.
export { BOUTON_CONTOUR, BOUTON_DANGER, BOUTON_PIED, BOUTON_PLEIN } from '../ui/boutons';

/**
 * L'échec d'une lecture, dans la zone qu'elle devait remplir (DESIGN-SYSTEM.md,
 * « Toasts » ; PARAMETRAGE-MODULES.md § 6) : le bandeau rouge de KLASYS, celui
 * des badges RFID et des photos d'élèves, avec son bouton « Réessayer » plein.
 * Sans `onReessayer` (accès refusé, fiche introuvable), pas de bouton :
 * réessayer ne changerait rien. `dansCarte` : posé en tête d'une carte qui a
 * déjà son cadre, il n'en ajoute pas un second.
 */
export const BandeauEchec = ({ children, onReessayer = null, dansCarte = false, className = '' }) => (
  <div
    role="alert"
    className={`flex flex-wrap items-center gap-3 bg-red-50 ${dansCarte ? 'border-b border-red-200 px-4 py-3' : 'rounded-xl border border-red-200 p-4'} ${className}`}
  >
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-100">
      <AlertTriangle size={15} className="text-red-600" />
    </div>
    <p className="min-w-0 flex-1 text-xs font-semibold text-red-700">{children}</p>
    {onReessayer && (
      <button
        type="button"
        onClick={onReessayer}
        className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3.5 py-2 text-xs font-semibold text-white transition-all hover:bg-red-700"
      >
        <RefreshCw size={13} /> Réessayer
      </button>
    )}
  </div>
);

/** Le badge d'état d'un actif, à l'arrondi d'un bouton, largeur fixe quand il se répète. */
export const BadgeEtat = ({ etat, fixe = false }) => (
  <span className={`inline-flex h-6 items-center justify-center whitespace-nowrap rounded-lg border px-2.5 text-xs font-medium ${fixe ? 'w-[104px]' : ''} ${ETATS[etat]?.teinte || 'border-gray-200 bg-gray-50 text-gray-600'}`}>
    {libelleEtat(etat)}
  </span>
);

/**
 * La liste déroulante du module, sur ListeDeroulante (celle de la Vie scolaire,
 * DESIGN-SYSTEM.md). `vide` ajoute en tête un vrai choix « rien » (« Aucun »,
 * « Tous les lieux ») ; `invite` n'est que le texte du champ tant que rien
 * n'est choisi (« Choisir… ») et ne se reprend pas dans la liste.
 */
export const ChampSelect = ({ id, valeur, onChange, options, vide = null, invite = 'Choisir…', disabled = false, 'aria-label': ariaLabel }) => (
  <ListeDeroulante
    id={id}
    valeur={valeur ?? ''}
    onChange={onChange}
    options={vide !== null ? [{ valeur: '', libelle: vide }, ...options] : options}
    placeholder={invite}
    desactive={disabled}
    aria-label={ariaLabel}
  />
);

/** Les employés actifs, « Nom - poste », pour un responsable ou un détenteur. */
export const optionsPersonnes = (personnes, saufId = null) => (personnes || [])
  .filter((p) => String(p.id) !== String(saufId ?? ''))
  .map((p) => ({ valeur: String(p.id), libelle: p.poste ? `${p.nom} - ${p.poste}` : p.nom }));

// ----------------------------------------------------------------------------
// La photo d'un actif : lue avec le jeton, jamais par une adresse publique
// ----------------------------------------------------------------------------

// Une photo ne change jamais (une nouvelle photo = un nouveau numéro) : on garde
// l'image lue pour toute la session, plutôt que de la relire à chaque passage.
const memoire = new Map();

function lirePhoto(photoId, mini) {
  const cle = `${photoId}:${mini ? 'mini' : 'grande'}`;
  if (!memoire.has(cle)) {
    const promesse = parc.photo(photoId, mini).then((blob) => URL.createObjectURL(blob));
    // Une lecture ratée ne reste pas en mémoire : la suivante réessaiera.
    promesse.catch(() => memoire.delete(cle));
    memoire.set(cle, promesse);
  }
  return memoire.get(cle);
}

/**
 * La photo, chargée seulement quand elle entre à l'écran : un registre de
 * cinquante lignes sur une connexion lente ne doit pas tirer cinquante photos
 * d'un coup.
 */
export const PhotoActif = ({ photoId, mini = false, className = '', alt = "Vue de l'actif", onClick }) => {
  const cadre = useRef(null);
  const [visible, setVisible] = useState(false);
  const [adresse, setAdresse] = useState(null);
  const [echec, setEchec] = useState(false);

  useEffect(() => {
    setAdresse(null);
    setEchec(false);
    if (!photoId) return undefined;
    const element = cadre.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observateur = new IntersectionObserver((entrees) => {
      if (entrees.some((e) => e.isIntersecting)) {
        setVisible(true);
        observateur.disconnect();
      }
    }, { rootMargin: '200px' });
    observateur.observe(element);
    return () => observateur.disconnect();
  }, [photoId]);

  useEffect(() => {
    if (!photoId || !visible) return undefined;
    let actif = true;
    lirePhoto(photoId, mini)
      .then((url) => { if (actif) setAdresse(url); })
      .catch(() => { if (actif) setEchec(true); });
    return () => { actif = false; };
  }, [photoId, mini, visible]);

  const base = `flex items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 ${className}`;
  if (!photoId) {
    return <div className={base}><span className="px-1 text-center text-[10px] font-medium text-gray-400">Sans photo</span></div>;
  }
  const image = adresse
    ? <img src={adresse} alt={alt} className="h-full w-full object-cover" />
    : <span className="px-1 text-center text-[10px] text-gray-400">{echec ? 'Photo illisible' : ''}</span>;
  return onClick && adresse ? (
    <button type="button" ref={cadre} onClick={onClick} className={`${base} cursor-zoom-in`} aria-label="Agrandir la photo">{image}</button>
  ) : (
    <div ref={cadre} className={base}>{image}</div>
  );
};

// ----------------------------------------------------------------------------
// Prendre ou choisir une photo
// ----------------------------------------------------------------------------

/**
 * Réduit la photo AVANT l'envoi : 1 600 px au plus, JPEG 82 %. Une photo de
 * téléphone de 4 à 8 Mo devient quelques centaines de Ko - la différence
 * entre un envoi qui passe et un envoi qui échoue sur une connexion lente.
 * Le serveur la réencode de toute façon ; en cas d'échec ici, l'original part.
 */
export async function compresserPhoto(fichier) {
  if (!fichier || !/^image\/(jpeg|png|webp)$/.test(fichier.type)) return fichier;
  try {
    const image = await new Promise((resoudre, rejeter) => {
      const img = new Image();
      const url = URL.createObjectURL(fichier);
      img.onload = () => { URL.revokeObjectURL(url); resoudre(img); };
      img.onerror = () => { URL.revokeObjectURL(url); rejeter(new Error('illisible')); };
      img.src = url;
    });
    const echelle = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
    if (echelle === 1 && fichier.size < 900 * 1024) return fichier;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.naturalWidth * echelle);
    canvas.height = Math.round(image.naturalHeight * echelle);
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resoudre) => canvas.toBlob(resoudre, 'image/jpeg', 0.82));
    if (!blob || blob.size >= fichier.size) return fichier;
    return new File([blob], 'photo.jpg', { type: 'image/jpeg' });
  } catch {
    return fichier;
  }
}

/**
 * Deux boutons : « Prendre une photo » ouvre la caméra dans la page, sur un
 * téléphone comme sur un ordinateur (FenetrePhotoCamera, demande de Daniel du
 * 04/11/2026) - un navigateur qui ne le permet pas passe par l'appareil photo
 * du téléphone (`capture`) ; « Choisir un fichier » ouvre la galerie ou le
 * disque.
 *
 * `accept` limité à JPEG, PNG et WEBP : un iPhone convertit alors sa photo
 * HEIC en JPEG avant de la donner à la page.
 */
export const ChampPhoto = ({ fichier, onChange, obligatoire = false, aide = null, disabled = false }) => {
  const camera = useRef(null);
  const galerie = useRef(null);
  const [apercu, setApercu] = useState(null);
  const [travail, setTravail] = useState(false);

  useEffect(() => {
    if (!fichier) { setApercu(null); return undefined; }
    const url = URL.createObjectURL(fichier);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [fichier]);

  const [cameraOuverte, setCameraOuverte] = useState(false);

  const prendre = async (choisi) => {
    if (!choisi) return;
    setTravail(true);
    try {
      onChange(await compresserPhoto(choisi));
    } finally {
      setTravail(false);
    }
  };

  const recevoir = (evenement) => {
    const choisi = evenement.target.files?.[0];
    evenement.target.value = '';
    prendre(choisi);
  };

  // La caméra s'ouvre dans la page ; un navigateur qui ne le permet pas
  // passe par l'appareil photo du téléphone (`capture`).
  const ouvrirCamera = () => (cameraDansLaPage() ? setCameraOuverte(true) : camera.current?.click());

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-start gap-3">
        <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
          {travail && <Loader2 className="h-5 w-5 animate-spin text-[#246990]" />}
          {!travail && apercu && <img src={apercu} alt="Aperçu avant l'envoi" className="h-full w-full object-cover" />}
          {!travail && !apercu && <span className="px-2 text-center text-[11px] text-gray-400">Aucune photo</span>}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm text-gray-700">
            Photo de l'actif{obligatoire ? <span className="font-semibold text-gray-900"> (obligatoire)</span> : ' (facultative)'}
          </p>
          {aide && <p className="text-[11px] leading-snug text-gray-500">{aide}</p>}
          <div className="flex flex-wrap gap-2">
            <Button size="default" type="button" className={`${BOUTON_CONTOUR} gap-2`} onClick={ouvrirCamera} disabled={disabled || travail}>
              {fichier ? <RotateCcw className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
              {fichier ? 'Reprendre' : 'Prendre une photo'}
            </Button>
            <Button size="default" type="button" className={`${BOUTON_CONTOUR} gap-2`} onClick={() => galerie.current?.click()} disabled={disabled || travail}>
              <ImagePlus className="h-4 w-4" />
              Choisir un fichier
            </Button>
          </div>
        </div>
      </div>
      <input ref={camera} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={recevoir} />
      <input ref={galerie} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={recevoir} />
      <FenetrePhotoCamera
        ouvert={cameraOuverte}
        onFermer={() => setCameraOuverte(false)}
        onPhoto={(photo) => { setCameraOuverte(false); prendre(photo); }}
      />
    </div>
  );
};

/** Une ligne « libellé : valeur » de la fiche. */
export const LigneFiche = ({ libelle, children }) => (
  <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 py-2 text-sm">
    <dt className="text-gray-500">{libelle}</dt>
    <dd className="min-w-0 break-words font-medium text-gray-800">{children || <span className="font-normal text-gray-400">-</span>}</dd>
  </div>
);
